import { css, html, nothing } from "lit";
import { query, state } from "lit/decorators.js";
import { ifDefined } from "lit-html/directives/if-defined.js";
import IMask from "imask/esm/imask.js";
import "imask/esm/masked/pattern.js";
import "../../atom/Icon/index.js";
import "../Input/index.js";
import "../Badge/index.js";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { preventDefault } from "../../../decorators/preventDefault.js";
import { convertToDate, isDateRangeValid, rangeHasChanged, formatLocaleDateRange } from "../dates.js";
import { getMaskOptions, parseDateRangeFromInput, getTimespanClasses, getDisplayPlaceholder } from "./utils.js";
import { dateRangeAttributeConverter } from "../attribute-converters.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
let DsfDateInput = class extends BaseComponent {
  constructor() {
    super(...arguments);
    this.value = [];
    this.disabled = false;
    this.dsf_mode = "single";
    this.dsf_locale = navigator.language;
    this.dsf_error = false;
    this.dsf_success = false;
    this.dsf_badge_square = false;
    this.dsf_size = "medium";
    this._displayValue = "";
    this._hasTimespanSlotContent = false;
  }
  get minDate() {
    return convertToDate(this.dsf_min_date);
  }
  get maxDate() {
    return convertToDate(this.dsf_max_date);
  }
  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;
    await this.dsfInputElement.updateComplete;
    this._mask = IMask(
      this.dsfInputElement.inputElement,
      getMaskOptions(this.dsf_locale, this.dsf_mode)
    );
    this._mask.on("accept", () => this.handleMaskInput(this._mask.value));
  }
  disconnectedCallback() {
    var _a, _b;
    super.disconnectedCallback();
    (_a = this._mask) == null ? void 0 : _a.off("accept");
    (_b = this._mask) == null ? void 0 : _b.destroy();
    this._mask = void 0;
  }
  update(changedProps) {
    if (changedProps.has("value")) {
      if (!this.value || !isDateRangeValid(this.value, this.dsf_mode, this.minDate, this.maxDate)) {
        this.value = [];
      }
      this.resetDisplayValue();
    }
    super.update(changedProps);
  }
  handleInput() {
  }
  handleMaskInput(value) {
    this._displayValue = value;
    const range = parseDateRangeFromInput(this._displayValue, this.dsf_locale, this.dsf_mode);
    const valid = isDateRangeValid(range, this.dsf_mode, this.minDate, this.maxDate);
    if (valid) {
      this.handleUserInput(range);
    }
  }
  handleInputBlur() {
    this.resetDisplayValue();
  }
  handleUserInput(newRange) {
    const oldValue = this.value;
    this.value = newRange;
    if (rangeHasChanged(oldValue, this.value)) {
      this.emit({ name: "input", payload: this.value });
    }
  }
  resetDisplayValue() {
    const formattedValue = formatLocaleDateRange(this.value, this.dsf_locale);
    if (this._mask) {
      this._mask.value = formattedValue;
    } else {
      this._displayValue = formattedValue;
    }
  }
  handleTimespanSlotChange() {
    this._hasTimespanSlotContent = !!this.timespanSlotElement && this.timespanSlotElement.assignedElements({ flatten: true }).length > 0;
  }
  render() {
    const timespanClasses = getTimespanClasses(this);
    const inputMaskPlaceholder = getDisplayPlaceholder(this.dsf_locale, this.dsf_mode);
    return html`
      <dsf-input
        class="min-w-0 flex-auto"
        .value=${this._displayValue}
        .disabled=${this.disabled}
        .dsf_error=${this.dsf_error}
        .dsf_success=${this.dsf_success}
        .dsf_message=${this.dsf_message}
        .dsf_label=${this.dsf_label}
        .dsf_name=${this.dsf_name}
        .dsf_placeholder=${inputMaskPlaceholder}
        .dsf_size=${this.dsf_size}
        dsf_type="text"
        dsf_icon_left=${ifDefined(this.dsf_icon)}
        @input=${this.handleInput}
        @blur=${this.handleInputBlur}
      >
        <div class=${timespanClasses}>
          ${this._hasTimespanSlotContent || this.dsf_badge ? html` <dsf-icon dsf_name="Clock" class="h-16 w-16"></dsf-icon> ` : nothing}
          <slot
            name="timespan"
            class="dsf-text-s whitespace-nowrap"
            @slotchange=${this.handleTimespanSlotChange}
          ></slot>
          ${this.dsf_badge ? html`<dsf-badge
                ?dsf_square=${this.dsf_badge_square}
                dsf_level=${this.disabled ? "disabled" : "default"}
                dsf_size="medium"
                >${this.dsf_badge}</dsf-badge
              >` : nothing}
        </div>
      </dsf-input>
    `;
  }
};
DsfDateInput.styles = [
  ...BaseComponent.styles,
  css`
      :host {
        display: inline-flex;
      }
    `
];
__decorateClass([
  prop({
    type: String,
    reflect: true,
    converter: dateRangeAttributeConverter
  })
], DsfDateInput.prototype, "value", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDateInput.prototype, "disabled", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfDateInput.prototype, "dsf_mode", 2);
__decorateClass([
  prop({ type: String })
], DsfDateInput.prototype, "dsf_locale", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDateInput.prototype, "dsf_error", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDateInput.prototype, "dsf_success", 2);
__decorateClass([
  prop({ type: String })
], DsfDateInput.prototype, "dsf_name", 2);
__decorateClass([
  prop({ type: String })
], DsfDateInput.prototype, "dsf_label", 2);
__decorateClass([
  prop({ type: String })
], DsfDateInput.prototype, "dsf_message", 2);
__decorateClass([
  prop({ type: String })
], DsfDateInput.prototype, "dsf_badge", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDateInput.prototype, "dsf_badge_square", 2);
__decorateClass([
  prop({ type: String })
], DsfDateInput.prototype, "dsf_min_date", 2);
__decorateClass([
  prop({ type: String })
], DsfDateInput.prototype, "dsf_max_date", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfDateInput.prototype, "dsf_icon", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfDateInput.prototype, "dsf_size", 2);
__decorateClass([
  query("dsf-input")
], DsfDateInput.prototype, "dsfInputElement", 2);
__decorateClass([
  query('slot[name="timespan"]')
], DsfDateInput.prototype, "timespanSlotElement", 2);
__decorateClass([
  state()
], DsfDateInput.prototype, "_displayValue", 2);
__decorateClass([
  state()
], DsfDateInput.prototype, "_hasTimespanSlotContent", 2);
__decorateClass([
  preventDefault
], DsfDateInput.prototype, "handleInput", 1);
DsfDateInput = __decorateClass([
  defineElement("dsf-date-input")
], DsfDateInput);
export {
  DsfDateInput
};
